import type { SupportedLocale as Lang } from "@site-types/content"
import { useRouterState } from "@tanstack/react-router"
import { defaultLocale, isSupportedLocale } from "@utils/locale"
import type { ReactNode } from "react"
import { createContext, useContext } from "react"

type RouteShellPage = string

export type RouteShellContextValue = {
  locale: Lang
  currentPage: RouteShellPage
  pageClass: string
  pathname: string
}

type RouteShellContextProviderProps = {
  children: ReactNode
  locale?: Lang
  page?: string
  pathname?: string
}

const DEFAULT_PATH = "/"

export const RouteShellContext = createContext<RouteShellContextValue | undefined>(undefined)

export function routeLocaleFromPathname(pathname: string): Lang {
  const firstSegment = pathname.split("?")[0]?.split("#")[0]?.split("/").filter(Boolean)[0]
  return isSupportedLocale(firstSegment) ? firstSegment : defaultLocale
}

export function routePageFromPathname(pathname: string): string {
  const segments = pathname.split("?")[0]?.split("#")[0]?.split("/").filter(Boolean)
  const page = segments[0] === "en" || segments[0] === "ja" ? segments[1] : segments[0]
  return page || "index"
}

export function routePageClass(page: string) {
  return page.replace(/[^a-z0-9_-]/gi, "-")
}

export function resolveRouteShellState({
  locale,
  page,
  pathname = DEFAULT_PATH,
}: {
  locale?: Lang
  page?: string
  pathname?: string
}): RouteShellContextValue {
  const safePathname = pathname || DEFAULT_PATH
  const derivedLocale = routeLocaleFromPathname(safePathname)
  const derivedPage = routePageFromPathname(safePathname)
  const finalLocale = locale ?? derivedLocale
  const finalPage = page ?? derivedPage
  return {
    locale: finalLocale,
    currentPage: finalPage,
    pageClass: routePageClass(finalPage),
    pathname: safePathname,
  }
}

export function useRouteShellPathname() {
  try {
    return useRouterState({ select: (state) => state.location.pathname })
  } catch {
    return typeof window === "undefined" ? DEFAULT_PATH : window.location.pathname || DEFAULT_PATH
  }
}

export function RouteShellContextProvider({
  children,
  locale,
  page,
  pathname,
}: RouteShellContextProviderProps) {
  const sourcePath = pathname ?? useRouteShellPathname()
  const value = resolveRouteShellState({ locale, page, pathname: sourcePath })

  return <RouteShellContext.Provider value={value}>{children}</RouteShellContext.Provider>
}

export function useRouteShellContext() {
  const context = useContext(RouteShellContext)
  const pathname = useRouteShellPathname()

  if (context) return context

  const routeShellContextMessage =
    "useRouteShellContext must be used within a RouteShellContextProvider. " +
    "Wrap the page with SiteShell to provide locale/page state."

  if (typeof process !== "undefined" && process.env.NODE_ENV !== "test") {
    if (typeof console !== "undefined" && import.meta.env?.DEV) {
      console.warn(routeShellContextMessage)
    }

    throw new Error(routeShellContextMessage)
  }

  return resolveRouteShellState({ pathname })
}
